// src/views/dashboard/money-flow/ClientDashboard.jsx
import React, { Component } from 'react';
import '../../../../styles/components/client-dashboard.scss';

import HeaderBanner from '../../../common/HeaderBanner';
import { SideBar } from '../../sidebar';
import i18n from '../../../../i18n';
import FilterSection from '../../../common/Filter';
import { selectStyles } from '../../../../styles/components/js/selectStyle';
import Select from 'react-select';
import DateRangeTags from '../../../common/DateRangeTags';
import { getGroupList } from '../Group/services/groupServices';
import { getBidders, getCashflowDashboard, getClientPayOutDetails, getClients } from './services/cashFlowService';
import CashFlowCards from './components/CashflowCards';
import { Building2, Clock3, Landmark, MoveRight, Users } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { OMR } from '../../../common/icons';
import DataTable from 'react-data-table-component';
import { tableStylesNew } from '../../../../styles/components/js/tableStyle';
import ClientPaymentSkeleton from './components/ClientPaymentSkeleton';
import { fmt } from '../../../../utils/common';
import Dropdown from '../../../common/DropDown';

function detectTag(startDate, endDate) {
  if (!startDate || !endDate) return 'custom';
  const presets = ['7', '30', '90', '365'];
  for (const tag of presets) {
    const range = DateRangeTags.resolveRange(tag);
    if (range.startDate === startDate && range.endDate === endDate) return tag;
  }
  return 'custom';
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
class CashFlowDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openModal: null,
      isLoading: false,
      hideSidebar: false,
      search: { account: '1', startDate: '', endDate: '', client: null, group: [], bidder: null },
      clients: [],
      groups: [],
      bidders: [],
      clientLoading: false,
      groupLoading: false,
      bidderLoading: false,
      page: 1,
      limit: 15,
      dateTag: '7',
      dashboardData: null,
      dashboardLoading: false,
      clientPayOutSummary: [],
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(window.location.search);
    const urlStart = params.get('startDate');
    const urlEnd = params.get('endDate');

    let startDate, endDate, dateTag;

    if (urlStart && urlEnd) {
      // URL has explicit dates — restore them and detect which tag they match
      startDate = urlStart;
      endDate = urlEnd;
      dateTag = detectTag(urlStart, urlEnd);
    } else {
      // No URL dates — default to last 7 days
      dateTag = '7';
      const range = DateRangeTags.resolveRange('7');
      startDate = range.startDate;
      endDate = range.endDate;
    }

    this.setState(
      {
        dateTag,
        search: {
          startDate,
          endDate,
          account: params.get('account') ? parseInt(params.get('account')) : '1',
          client: params.get('client') ? parseInt(params.get('client')) : null,

          group: params.get('group')
            ? params
              .get('group')
              .split(',')
              .map((id) => parseInt(id))
            : [],

          bidder: params.get('bidder') ? parseInt(params.get('bidder')) : null,
        },
        page: parseInt(params.get('page')) || 1,
        limit: parseInt(params.get('limit')) || 15,
      },
      () => {
        this.fetchClientList();
        this.fetchGroupList();
        this.fetchBidderList();
        this.fetchDashboard();
        this.fetchClientPayOutDeatails();
      },
    );
  }

  // Update URL without refreshing page
  updateUrlQuery(q) {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== '' && v !== null)),
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }

  async fetchDashboard() {
    this.setState({ isLoading: true });
    try {
      const { startDate, endDate, account, bidder, client, group } = this.state.search;
      const q = {};
      if (startDate) q.startDate = startDate;
      if (endDate) q.endDate = endDate;
      if (account) q.account = account;
      if (bidder) q.bidder = bidder;
      if (client) q.client = client;
      if (group?.length) q.group = group.join(',');

      this.updateUrlQuery(q);

      const response = await getCashflowDashboard(q); // your API model fn
      if (response.success) {
        this.setState({ dashboardData: response.data, isLoading: false });
      } else {
        this.setState({ isLoading: false });
      }
    } catch {
      this.setState({ isLoading: false });
    }
  }
  async fetchClientPayOutDeatails() {
    this.setState({ isLoading: true });
    try {
      let { limit, page } = this.state;
      const { startDate, endDate, account, bidder, client, group } = this.state.search;
      const q = {
        page: page,
        limit: limit,
      };
      if (startDate) q.startDate = startDate;
      if (endDate) q.endDate = endDate;
      if (account) q.account = account;
      if (bidder) q.bidder = bidder;
      if (client) q.client = client;
      if (group?.length) q.group = group.join(',');

      this.updateUrlQuery(q);

      const response = await getClientPayOutDetails(q); // your API model fn
      if (response.success) {
        this.setState({
          clientPayOutSummary: response,
          isLoading: false,
        });
      } else {
        this.setState({ isLoading: false });
      }
    } catch {
      this.setState({ isLoading: false });
    }
  }
  async fetchClientList() {
    this.setState({ clientLoading: true });
    try {
      const { startDate, endDate } = this.state.search;
      const q = { type: 'cash-flow' };
      if (startDate) q.startDate = startDate;
      if (endDate) q.endDate = endDate;
      const response = await getClients(q);
      if (response.success) {
        const clients = [
          {
            value: null,
            label: i18n.t('all') || 'All',
          },
          ...response.data.map((item) => ({
            value: item.id,
            label: item.organization_name,
          })),
        ];

        this.setState({
          clients,
          clientLoading: false,
        });
      } else {
        this.setState({ clientLoading: false });
      }
    } catch {
      this.setState({ clientLoading: false });
    }
  }

  async fetchGroupList() {
    this.setState({ groupLoading: true });
    const { startDate, endDate, client } = this.state.search;
    const q = { type: 'cash-flow' };
    if (startDate) q.startDate = startDate;
    if (endDate) q.endDate = endDate;
    if (client) q.organization = client;

    const response = await getGroupList(q);
    if (response.success) {
      const groups = [
        {
          value: null,
          label: i18n.t('all') || 'All',
          organization: null,
        },
        ...response.data.map((item) => ({
          value: item.id,
          label: item.group_name,
          organization: item.organization,
        })),
      ];

      this.setState({
        groups,
        groupLoading: false,
      });
    } else {
      this.setState({ groupLoading: false });
    }
  }

  async fetchBidderList() {
    this.setState({ bidderLoading: true });
    const { startDate, endDate, client, group } = this.state.search;

    const q = { type: 'cashFlow' };
    if (client) q.client = client;
    if (group?.length) q.group = group.join(',');
    if (startDate) q.startDate = startDate;
    if (endDate) q.endDate = endDate;

    const response = await getBidders(q);
    if (response.success) {
      const bidders = [
        {
          value: null,
          label: i18n.t('all') || 'All',
        },
        ...response.data.map((item) => ({
          value: item.id,
          label: `${item.name} (${item.id})`,
        })),
      ];

      this.setState({
        bidders,
        bidderLoading: false,
      });
    } else {
      this.setState({ bidderLoading: false });
    }
  }

  handleDateTag = (tag, range) => {
    this.setState(
      (prev) => ({
        dateTag: tag,
        search: {
          ...prev.search,
          ...range,
        },
      }),
      () => {
        this.fetchClientList();
        this.fetchGroupList();
        this.fetchBidderList();
      },
    );
  };

  //  Client onChange — store as integer, clear groups, load group list
  handleClientChange = (selectedOption) => {
    const clientValue = selectedOption ? parseInt(selectedOption.value) : null;
    this.setState(
      (prevState) => ({
        search: {
          ...prevState.search,
          client: clientValue,
          group: [],
          bidder: null,
        },
        groups: [],
        bidders: [],
      }),
      async () => {
        await this.fetchGroupList();
        await this.fetchBidderList();
      },
    );
  };

  //  Group onChange — store as integer
  handleGroupChange = (selectedOptions) => {
    const selectedGroups = selectedOptions || [];

    // First selected group's organization becomes client
    const client = selectedGroups.length ? parseInt(selectedGroups[0].organization) : null;

    // Keep only groups from same organization
    const validGroups = selectedGroups.filter((item) => parseInt(item.organization) === client);

    const groupValues = validGroups.map((item) => parseInt(item.value));

    this.setState(
      (prevState) => ({
        search: {
          ...prevState.search,
          client,
          group: groupValues,
          bidder: null,
        },
        bidders: [],
      }),
      async () => {
        // Reload groups filtered by selected client
        await this.fetchGroupList();

        // Reload bidders
        await this.fetchBidderList();
      },
    );
  };

  //  Bidder onChange — store as integer
  handleBidderChange = (selectedOption) => {
    this.setState((prevState) => ({
      search: {
        ...prevState.search,
        bidder: selectedOption ? parseInt(selectedOption.value) : null,
      },
    }));
  };

  handleSearchChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      dateTag: name === 'startDate' || name === 'endDate' ? 'custom' : prev.dateTag,
      search: { ...prev.search, [name]: value },
    }));
  };
  handleSearch = () => {
    this.fetchDashboard();
    this.fetchClientPayOutDeatails();
  };

  handleClear = () => {
    const range = DateRangeTags.resolveRange('7');
    this.setState(
      {
        dateTag: '7',
        search: {
          startDate: range.startDate,
          endDate: range.endDate,
          account: '1',
          client: null,
          group: [],
          bidder: null,
        },
      },
      () => {
        this.fetchClientList();
        this.fetchGroupList();
        this.fetchBidderList();
        this.fetchDashboard();
        this.fetchClientPayOutDeatails();
      },
    );
  };

  getPrimaryColumns = () => {
    const { search } = this.state;
    return [
      {
        name: i18n.t('sl'),
        selector: (row) => row.sl,
        // width: '80px',
        center: true,
        sortable: false,

        cell: (row, index) => <span className="sl-number">{index + 1}</span>,
      },
      {
        name: i18n.t('client'),
        selector: (row) => row.organization,
        // width: '130px',
        sortable: false,
        cell: (row) => <span className="remark-text">{row.organization.name}</span>,
      },
      {
        name: i18n.t('total_client_amount'),
        selector: (row) => row.amount,
        // width: '160px',
        right: true,
        sortable: false,
        cell: (row) => (
          <AmountCell
            amount={row.total_client_amount.amount}
            count={row.total_client_amount.auction_count}
            color="#1b8f9b"
          />
        ),
      },
      {
        name: i18n.t('client_amount_paid'),
        selector: (row) => row.paid_client_amount.amount,
        // width: '160px',
        right: true,
        sortable: false,
        cell: (row) => (
          <AmountCell
            amount={row.paid_client_amount.amount}
            count={row.paid_client_amount.auction_count}
            color="#10b981"
          />
        ),
      },
      {
        name: i18n.t('pending_client_due'),
        selector: (row) => row.pending_client_amount.amount,
        // width: '160px',
        right: true,
        sortable: false,
        cell: (row) => (
          <AmountCell
            amount={row.pending_client_amount.amount}
            count={row.pending_client_amount.auction_count}
            color="#ef4444"
          />
        ),
      },
      {
        name: i18n.t('action'),
        selector: (row) => row.organization.id,
        right: true,

        cell: (row) => (
          <NavLink
            to={{
              pathname: '/cash-flow/auction-payment/client/details',

              search: new URLSearchParams(
                Object.fromEntries(
                  Object.entries({
                    startDate: search?.startDate,
                    endDate: search?.endDate,
                    account: search?.account,        // ← added
                    bidder: search?.bidder,
                    group: search?.group?.length ? search.group.join(',') : null,
                    client: row?.organization?.id,
                    status: 'client_pending',
                  }).filter(([_, v]) => v !== null && v !== '' && v !== undefined),
                ),
              ).toString(),
            }}
            className="details-link"
          >
            {i18n.t('details')}
            <span className="arrow">
              <MoveRight strokeWidth={1} />
            </span>
          </NavLink>
        ),
      },
    ];
  };

  render() {
    const {
      clients,
      clientLoading,
      groups,
      groupLoading,
      bidders,
      bidderLoading,
      search,
      hideSidebar,
      dashboardData,
      isLoading,
      clientPayOutSummary,
    } = this.state;
    const { startDate, endDate, account } = search;
    const clientPayOutData = clientPayOutSummary?.data || [];
    const clientPayOutMeta = clientPayOutSummary?.meta || null;

    return (
      <React.Fragment>
        <HeaderBanner title={i18n.t('mzadcom_cash_flow_dashboard')} subtitle={i18n.t('cash_flow_dashboard_subtitle')} />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div className={hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'}>
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      >
                        {hideSidebar ? <i className="icon-size-actual" /> : <i className="icon-size-fullscreen" />}
                      </span>
                    </div>
                  </div>

                  <div className="card-body">
                    <DateRangeTags activeTag={this.state.dateTag} onChange={this.handleDateTag} />
                    <FilterSection loading={isLoading} onSearch={this.handleSearch} onClear={this.handleClear}>
                      {/* Account Type  Dropdown */}
                      <Dropdown
                        name="account"
                        value={account}
                        onChange={this.handleSearchChange}
                        placeholder={i18n.t('account_type')}
                        empty={i18n.t('select_account')}
                        options={[
                          { value: '1', label: i18n.t('mzadcom_account') },
                          { value: '2', label: i18n.t('client_account') },
                        ]}
                      />
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="startDate"
                          type="date"
                          name="startDate"
                          value={startDate}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="startDate" className="floating-label">
                          {i18n.t('start_date')}
                        </label>
                      </div>
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="endDate"
                          type="date"
                          name="endDate"
                          value={endDate}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="endDate" className="floating-label">
                          {i18n.t('end_date')}
                        </label>
                      </div>
                      <div className="react-select-wrapper">
                        <Select
                          options={clients}
                          value={clients.find((c) => c.value === this.state.search.client) || null}
                          onChange={this.handleClientChange}
                          name="client"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={clientLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                          isLoading={clientLoading}
                          isDisabled={clientLoading}
                          isClearable
                        />
                        <label className="react-select-label">{i18n.t('client') || 'Client'}</label>
                      </div>

                      {/* Group Dropdown */}
                      <div className="react-select-wrapper">
                        <Select
                          options={groups}
                          value={groups.filter((c) => this.state.search.group?.includes(c.value))}
                          onChange={this.handleGroupChange}
                          name="group"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={groupLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                          isLoading={groupLoading}
                          isDisabled={groupLoading}
                          isMulti
                          isClearable
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                        />
                        <label className="react-select-label">{i18n.t('group_name')}</label>
                      </div>

                      {/* Bidder Dropdown */}
                      <div className="react-select-wrapper">
                        <Select
                          options={bidders}
                          value={bidders.find((c) => c.value === this.state.search.bidder) || null}
                          onChange={this.handleBidderChange}
                          name="bidder"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={bidderLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                          isLoading={bidderLoading}
                          isDisabled={bidderLoading}
                          isClearable
                        />
                        <label className="react-select-label">{i18n.t('bidder')}</label>
                      </div>
                    </FilterSection>
                    {isLoading ? (
                      <ClientPaymentSkeleton />
                    ) : (
                      <>
                        <CashFlowCards data={dashboardData} search={search} />
                        {search?.account !== '2' && (
                          <div className="cashflow out">
                            <div className="cf-details column">
                              {/* Refund Pending Section */}
                              <div className="cf-section-2">
                                <div className="cf-section__title pending">
                                  <div className="cf-main-icon red">
                                    <Clock3 size={20} strokeWidth={1.8} />
                                  </div>
                                  <span>{i18n.t('pending_client_due_vat_transfers')}</span>
                                </div>

                                <div className="cf-card-grid">
                                  {/* Card 1 */}
                                  <NavLink
                                    to={{
                                      pathname: '/cash-flow/auction-payment/client/details',
                                      search: new URLSearchParams(
                                        Object.fromEntries(
                                          Object.entries({
                                            startDate: search?.startDate,
                                            endDate: search?.endDate,
                                            account: search?.account,          // ← added
                                            bidder: search?.bidder,
                                            client: search?.client,
                                            group: search?.group?.length ? search.group.join(',') : null,
                                            status: 'client_pending',          // 'client_completed' on Card 3
                                          }).filter(([_, v]) => v !== null && v !== '' && v !== undefined),
                                        ),
                                      ).toString(),
                                    }}
                                    className="cf-stat-card pending"
                                  >
                                    <div className="cf-stat-card__top">
                                      <div className="cf-stat-card__icon users pending">
                                        <Users size={18} />
                                      </div>
                                    </div>
                                    <h4 className="cf-stat-card__title">{i18n.t('pending_client_due')}</h4>
                                    <h3 className="cf-stat-card__value pending">
                                      {fmt(clientPayOutMeta?.totals?.pending_client_amount || 0)}
                                      <OMR size={12} color={'#d97706'} />
                                    </h3>

                                    <p className="cf-stat-card__label">{i18n.t('final_bid_amounts_vat_to_clients')}</p>
                                  </NavLink>

                                  {/* Card 2 */}
                                  <NavLink
                                    to={{
                                      pathname: '/cash-flow/auction-payment/client/details',
                                      search: new URLSearchParams(
                                        Object.fromEntries(
                                          Object.entries({
                                            startDate: search?.startDate,
                                            endDate: search?.endDate,
                                            bidder: search?.bidder,
                                            account: search?.account,          // ← added
                                            client: search?.client,
                                            group: search?.group?.length ? search.group.join(',') : null,
                                            status: 'client_pending',
                                          }).filter(([_, v]) => v !== null && v !== '' && v !== undefined),
                                        ),
                                      ).toString(),
                                    }}
                                    className="cf-stat-card pending"
                                  >
                                    <div className="cf-stat-card__top">
                                      <div className="cf-stat-card__icon amount pending">
                                        <Landmark size={18} />
                                      </div>
                                    </div>
                                    <h4 className="cf-stat-card__title">{i18n.t('vat_pending')}</h4>
                                    <h3 className="cf-stat-card__value pending">
                                      {fmt(clientPayOutMeta?.totals?.pending_service_charge_vat || 0)}
                                      <OMR size={12} color={'#d97706'} />
                                    </h3>

                                    <p className="cf-stat-card__label">{i18n.t('service_charge_vat_to_vat_account')}</p>
                                  </NavLink>
                                  {/* Card 3 */}
                                  <NavLink
                                    to={{
                                      pathname: '/cash-flow/auction-payment/client/details',
                                      search: new URLSearchParams(
                                        Object.fromEntries(
                                          Object.entries({
                                            startDate: search?.startDate,
                                            endDate: search?.endDate,
                                            bidder: search?.bidder,
                                            client: search?.client,
                                            account: search?.account,          // ← added
                                            group: search?.group,
                                            status: 'client_completed',
                                          }).filter(([_, v]) => v !== null && v !== '' && v !== undefined),
                                        ),
                                      ).toString(),
                                    }}
                                    className="cf-stat-card pending"
                                  >
                                    <div className="cf-stat-card__top">
                                      <div className="cf-stat-card__icon amount pending">
                                        <Building2 size={18} />
                                      </div>
                                    </div>
                                    <h4 className="cf-stat-card__title">{i18n.t('profit')}</h4>
                                    <h3 className="cf-stat-card__value pending">
                                      {clientPayOutMeta?.totals?.pending_service_charge || 0}
                                      <OMR size={12} color={'#d97706'} />
                                    </h3>

                                    <p className="cf-stat-card__label">{i18n.t('auction_service_charge_revenue')}</p>
                                  </NavLink>
                                </div>

                                <div className="payment-summary-container">
                                  <div className="summary-header">
                                    <div className="psm-header-title">
                                      <h1>{i18n.t('outstanding_client_due')}</h1>
                                      <p className="header-subtitle-w3">
                                        {i18n.t('detailed_client_payment_pending_summary')}
                                      </p>
                                    </div>
                                  </div>

                                  {/* DataTable Component */}
                                  <div className="payment-table-wrapper">
                                    <DataTable
                                      responsive={true}
                                      columns={this.getPrimaryColumns()}
                                      data={clientPayOutData}
                                      customStyles={tableStylesNew}
                                      highlightOnHover
                                    />
                                  </div>

                                  {/* Summary Footer */}
                                  <div className="summary-footer">
                                    <div className="footer-section-w3 ">
                                      <div className="section-label">{i18n.t('client_invoice')}</div>
                                      <div className="amount-display"></div>
                                    </div>

                                    <div className="footer-section-w3 total-section">
                                      <div className="section-label">{i18n.t('total_invoice')}</div>
                                      <div className="amount-display">
                                        {fmt(clientPayOutMeta?.totals?.total_client_amount || 0)}
                                      </div>
                                    </div>

                                    <div className="footer-section-w3 vat-section">
                                      <div className="section-label">{i18n.t('total_amount_paid')}</div>
                                      <div className="amount-display">
                                        {fmt(clientPayOutMeta?.totals?.paid_client_amount || 0)}
                                      </div>
                                    </div>

                                    <div className="footer-section-w3 balance-section">
                                      <div className="section-label">{i18n.t('balance_to_pay')}</div>
                                      <div className="amount-display highlight">
                                        {fmt(clientPayOutMeta?.totals?.pending_client_amount || 0)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default CashFlowDashboard;
export const AmountCell = ({ amount, count, color = '#1b8f9b' }) => {
  return (
    <div className="amount-cell">
      <div className="d-flex flex-column align-items-end">
        {/* amount + OMR */}
        <div className="d-flex align-items-center gap-1">
          <span className="amount-value" style={{ color }}>
            {amount}
          </span>
          <OMR size={10} color={color} />
        </div>

        {/* auction count */}
        <small>
          <span className="auction-count">{count}</span>
          <span className="auction-label">{i18n.t('auctions')}</span>
        </small>
      </div>
    </div>
  );
};
